import { useState, useEffect } from 'react';
import { MessageCircle, Send, CheckCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { api } from '../services/api';
import type { Comment } from '../types';

interface CourseCommentsProps {
  courseId: string;
}

export default function CourseComments({ courseId }: CourseCommentsProps) {
  const { theme } = useTheme();
  const { font } = useFont();
  const { user } = useAuth();
  const { hasActiveSubscription } = useSubscription();
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [courseComments, setCourseComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!hasActiveSubscription()) {
      setLoading(false);
      return;
    }

    api.getCourseComments(courseId)
      .then(comments => {
        setCourseComments(comments);
      })
      .catch(error => {
        console.error('Error loading course comments:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [courseId]); // Charge quand le courseId change

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await api.addCourseComment(courseId, newComment.trim());
      setNewComment('');
      setSuccessMessage('Commentaire ajouté avec succès !');

      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

      // Refresh course comments
      const updatedComments = await api.getCourseComments(courseId);
      setCourseComments(updatedComments);
    } catch (error) {
      console.error('Error adding comment:', error);
      setSuccessMessage('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!hasActiveSubscription()) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mt-8" style={{ fontFamily: font.family }}>
      <div className="flex items-center mb-6">
        <MessageCircle className={`h-6 w-6 text-${theme.text} mr-3`} />
        <h3 className="text-xl font-semibold">Commentaires</h3>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
          <span className="text-green-700 text-sm font-medium">{successMessage}</span>
        </div>
      )}

      {/* User's comments */}
      {!loading && courseComments.length > 0 && (
        <div className="mb-6">
          <h4 className="text-lg font-medium mb-4">Mes commentaires sur ce cours</h4>
          <div className="space-y-4">
            {courseComments.map((comment, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-800 mb-2">{comment.content}</p>
                <p className="text-sm text-gray-500">{formatDate(comment.date)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add comment form */}
      <div className="border-t border-gray-100 pt-6">
        <h4 className="text-lg font-medium mb-4">Ajouter un commentaire</h4>
      <form onSubmit={handleSubmitComment} className="mb-6">
        <div className="flex items-start space-x-4">
          {user?.picture && !imageError ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-10 h-10 rounded-full object-cover border border-gray-200"
              onError={handleImageError}
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-lg">
              😊
            </div>
          )}
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Partagez votre avis sur ce cours..."
              className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              rows={3}
              maxLength={500}
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-gray-500">
                {newComment.length}/500 caractères
              </span>
              <button
                type="submit"
                disabled={!newComment.trim() || isSubmitting}
                className={`
                  flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                  ${newComment.trim() && !isSubmitting
                    ? `bg-${theme.primary} text-white hover:opacity-90`
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }
                `}
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {isSubmitting ? 'Envoi...' : 'Publier'}
              </button>
            </div>
          </div>
        </div>
      </form>
      </div>
    </div>
  );
}